'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const isActive = (href: string) => {
    if (href === '/#services') return pathname === '/';
    return pathname === href;
  };

  const isWebyLanding = pathname === '/weby-na-miru';
  const isEshopyLanding = pathname === '/eshopy-na-miru';
  const isBezRizika = pathname === '/web-bez-rizika';
  const isLandingPage = isWebyLanding || isEshopyLanding || isBezRizika;

  const navItems = isWebyLanding
    ? [
        { href: '#why', label: 'Výhody' },
        { href: '#portfolio', label: 'Portfolio' },
        { href: '#cenik', label: 'Ceník' },
      ]
    : isEshopyLanding
    ? [
        { href: '#why', label: 'Výhody' },
        { href: '#cms-demo', label: 'CMS' },
        { href: '#cenik', label: 'Ceník' },
      ]
    : isBezRizika
    ? [
        { href: '#jak-to-funguje', label: 'Jak to funguje' },
        { href: '#portfolio', label: 'Portfolio' },
        { href: '#cenik', label: 'Ceník' },
      ]
    : [
        { href: '/#services', label: 'Služby' },
        { href: '/portfolio', label: 'Portfolio' },
        { href: '/cenik', label: 'Ceník' },
      ];

  const ctaHref = isLandingPage ? '#kontakt' : '/kontakt';

  const hasDarkHeader = pathname === '/' || pathname === '/portfolio' || pathname === '/weby-na-miru' || pathname === '/eshopy-na-miru' || pathname === '/web-bez-rizika';

  const navClasses = [
    isOpen ? 'toggled' : '',
    scrolled ? 'scrolled' : '',
    hasDarkHeader ? 'dark-header' : '',
    pathname === '/' ? 'home' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <nav className={navClasses || undefined}>
        <div className="nav-content">
          <div className="box">
            <div className="logo">
              <Link href="/" onClick={closeMenu}>jakubmatuška</Link>
            </div>
          </div>
          <div className="nav-links">
            {navItems.map((item) => (
              item.href.startsWith('#') ? (
                <a key={item.href} href={item.href}>{item.label}</a>
              ) : (
                <Link key={item.href} href={item.href} className={isActive(item.href) ? 'active' : undefined}>{item.label}</Link>
              )
            ))}
            {ctaHref.startsWith('#') ? (
              <a href={ctaHref} className="nav-cta">Nezávazná poptávka</a>
            ) : (
              <Link href={ctaHref} className={`nav-cta${isActive(ctaHref) ? ' active' : ''}`}>Nezávazná poptávka</Link>
            )}
          </div>
          <div className="box burger-box">
            <button
              className={`burger ${isOpen ? 'toggled' : ''}`}
              id="burger"
              onClick={toggleMenu}
              aria-label={isOpen ? 'Zavřít menu' : 'Otevřít menu'}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              type="button"
            >
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>

      <div id="mobile-menu" className={`menu ${isOpen ? 'toggled' : ''}`} role="dialog" aria-modal="true" aria-label="Navigační menu">
        <div className={`menu-line ${isOpen ? 'toggled' : ''}`} aria-hidden="true"></div>
        <div className={`menu-line ${isOpen ? 'toggled' : ''}`}>
          <nav aria-label="Hlavní navigace">
          <ul>
            {navItems.map((item) => (
              <li key={item.href}>
                {item.href.startsWith('#') ? (
                  <a href={item.href} onClick={closeMenu}>{item.label}</a>
                ) : (
                  <Link href={item.href} onClick={closeMenu}>{item.label}</Link>
                )}
              </li>
            ))}
            <li>
              {ctaHref.startsWith('#') ? (
                <a href={ctaHref} onClick={closeMenu}>Kontakt</a>
              ) : (
                <Link href={ctaHref} onClick={closeMenu}>Kontakt</Link>
              )}
            </li>
            <li>
              <a href="tel:+420774801300" className="contact-me">+420 774801300</a>
            </li>
            <li>
              <a href="mailto:hi@jakubmatuska.cz" className="contact-me">hi@jakubmatuska.cz</a>
            </li>
            <li>
              <a target="_blank" href="https://www.facebook.com/jakubmatuskaweb" className="soc-icon" rel="noopener noreferrer">
                <Image width={50} height={50} src="/images/icons/fb.png" alt="Facebook" style={{ marginRight: '5px', marginBottom: '2px' }} /> Facebook
              </a>
            </li>
            <li>
              <a target="_blank" href="https://www.instagram.com/jakubmatuskaweb/" className="soc-icon" rel="noopener noreferrer">
                <Image width={50} height={50} src="/images/icons/ig.png" alt="Instagram" />Instagram
              </a>
            </li>
            <li>
              <a target="_blank" href="https://www.linkedin.com/in/jakub-matu%C5%A1ka-88522a91/" className="soc-icon" rel="noopener noreferrer">
                <Image width={50} height={50} src="/images/icons/linkedin.png" alt="LinkedIn" />LinkedIn
              </a>
            </li>
          </ul>
          </nav>
        </div>
        <div className={`menu-line ${isOpen ? 'toggled' : ''}`} aria-hidden="true"></div>
        <div className={`menu-line ${isOpen ? 'toggled' : ''}`} aria-hidden="true"></div>
      </div>
    </>
  );
}
